/**
 * Jobs Integration Service
 * 
 * Bridges the existing OptimizationEventService and WFAEventService SSE events 
 * to the unified Jobs store. This allows optimization and WFA jobs to appear 
 * in the JobsPanel and JobsConsole.
 */

import { useJobsStore } from '../../store/jobsStore';
import { optimizationEventService } from '../optimization/OptimizationEventService';
import type { OptimizationEvent } from '../optimization/OptimizationEventService';
import { wfaEventService } from '../wfa/WFAEventService';
import type { WFAEvent, WFAProgressData } from '../wfa/WFAEventService';
import {
    mapOptimizationEventToJobUpdate,
    createJobFromOptimization,
    type CreateJobFromOptimizationParams,
} from './optimizationJobAdapter';
import {
    mapWFAEventToJobUpdate,
    createJobFromWFA,
    type WFAJobRegistrationParams,
    type WFAEvent as WFAJobEvent,
} from './wfaJobAdapter';
import type { Job, JobStatus, JobProgress, JobLogLine } from '../../types/jobs';

/**
 * Service that wires up optimization and WFA events to the Jobs store
 */
export class JobsIntegrationService {
    private running = false;
    private unsubscribeOptimization: (() => void) | null = null;
    private unsubscribeWFA: (() => void) | null = null;

    /**
     * Start listening to optimization and WFA events and syncing to Jobs store
     */
    start(): void {
        if (this.running) {
            return;
        }

        // Connect to Optimization SSE
        optimizationEventService.connect();
        this.unsubscribeOptimization = optimizationEventService.subscribeGlobal(
            this.handleOptimizationEvent.bind(this)
        );
        
        // Connect to WFA SSE
        wfaEventService.connect();
        this.unsubscribeWFA = wfaEventService.subscribeGlobal(
            this.handleWFAEvent.bind(this)
        );
        
        this.running = true;
    }

    /**
     * Stop listening to events
     */
    stop(): void {
        if (!this.running) {
            return;
        }

        if (this.unsubscribeOptimization) {
            this.unsubscribeOptimization();
            this.unsubscribeOptimization = null;
        }
        
        if (this.unsubscribeWFA) {
            this.unsubscribeWFA();
            this.unsubscribeWFA = null;
        }
        
        this.running = false;
    }

    /**
     * Check if the service is running
     */
    isRunning(): boolean {
        return this.running;
    }

    /**
     * Register a new optimization job directly (call when starting optimization)
     */
    registerOptimizationJob(params: CreateJobFromOptimizationParams): void {
        const job = createJobFromOptimization(params);
        useJobsStore.getState().addJob(job);
    }

    /**
     * Register a new WFA job directly (call when starting WFA)
     * Also generates initial log entries with configuration details
     */
    registerWFAJob(jobId: string, params: WFAJobRegistrationParams): void {
        const store = useJobsStore.getState();
        
        // Create the job with basic params
        const job = createJobFromWFA({ 
            jobId, 
            symbol: params.symbol,
            interval: params.interval,
            strategyName: params.strategyName,
            totalPeriods: params.totalPeriods,
        });
        store.addJob(job);
        
        // Attach console to this job so logs appear immediately
        store.attachConsole(jobId);
        
        // Generate initial log entries with configuration details
        const logs: JobLogLine[] = [];
        const timestamp = new Date().toISOString();
        
        logs.push({
            timestamp,
            level: 'info',
            message: 'Initializing Walk-Forward Analysis...',
        });
        
        // Strategy and asset info
        logs.push({
            timestamp,
            level: 'info',
            message: `Strategy: ${params.strategyName || 'Unknown'} | ${params.symbol} ${params.interval}`,
        });
        
        // Selection info if provided
        if (params.selectionMode && params.includedCount !== undefined && params.totalCount !== undefined) {
            const modeLabel = params.selectionMode.charAt(0).toUpperCase() + params.selectionMode.slice(1);
            logs.push({
                timestamp,
                level: 'info',
                message: `Selection: ${modeLabel} (${params.includedCount} / ${params.totalCount} configs)`,
            });
        }
        
        // WFA settings
        if (params.ratio) {
            logs.push({
                timestamp,
                level: 'info',
                message: `Training:Testing Ratio: ${params.ratio} | Rolling: ${params.rollingWindows ? 'Yes' : 'No'}`,
            });
        }
        
        // Window configuration
        if (params.trainingCandles && params.testingCandles && params.periodLength) {
            logs.push({
                timestamp,
                level: 'info',
                message: `Window: ${params.trainingCandles} training + ${params.testingCandles} testing = ${params.periodLength} candles`,
            });
        }
        
        // Step size
        if (params.stepCandles !== undefined) {
            const stepMode = params.stepSizeMode === 'auto' ? 'auto - half testing window' : 'manual';
            logs.push({
                timestamp,
                level: 'info',
                message: `Step Size: ${params.stepCandles} candles (${stepMode})`,
            });
        }
        
        // Trade estimates
        if (params.estimatedTestingTrades !== undefined) {
            const source = params.tradeEstimateSource || 'estimate';
            const confidence = params.tradeEstimateConfidence || 'medium';
            logs.push({
                timestamp,
                level: 'info',
                message: `Est. ${params.estimatedTestingTrades} trades per testing window (${confidence} confidence, from ${source})`,
            });
        }
        
        // Warning if any
        if (params.warning) {
            logs.push({
                timestamp,
                level: 'warn',
                message: `Note: ${params.warning}`,
            });
        }
        
        // Job started confirmation
        logs.push({
            timestamp,
            level: 'success',
            message: `Job started: ${jobId.substring(0, 8)}...`,
        });
        
        // Append all logs
        store.appendLogs(logs);
    }

    /**
     * Handle a WFA event from the SSE stream
     */
    private handleWFAEvent(event: WFAEvent): void {
        // Skip non-job events (heartbeat, connected, etc.)
        if (!event.jobId || event.type === 'wfa-connected') {
            return;
        }
        
        console.log('[JobsIntegration] WFA event received:', {
            type: event.type,
            jobId: event.jobId,
            data: event.data,
        });
        
        const store = useJobsStore.getState();
        const existingJob = store.jobs.get(event.jobId);

        if (!existingJob) {
            // Job doesn't exist - create a placeholder
            const placeholderJob = this.createWFAPlaceholderJob(event);
            store.addJob(placeholderJob);
        }

        // Convert to adapter event format and apply update
        const adapterEvent = this.convertToAdapterEvent(event);
        if (adapterEvent) {
            const update = mapWFAEventToJobUpdate(adapterEvent);
            console.log('[JobsIntegration] WFA update:', { jobId: event.jobId, update });
            this.applyWFAJobUpdate(event.jobId, update);
        }
        
        // Create a log entry for the console if this job is attached
        this.createWFALogEntry(event);
    }
    
    /**
     * Convert WFAEvent from service format to adapter format
     */
    private convertToAdapterEvent(event: WFAEvent): WFAJobEvent | null {
        if (!event.jobId) return null;
        
        // Map event types
        let adapterType: WFAJobEvent['type'];
        switch (event.type) {
            case 'wfa-progress':
                adapterType = 'wfa-progress';
                break;
            case 'wfa-completed':
                adapterType = 'wfa-completed';
                break;
            case 'wfa-failed':
            case 'wfa-error':
                adapterType = 'wfa-error';
                break;
            case 'wfa-queued':
                adapterType = 'wfa-queued';
                break;
            case 'wfa-cancelled':
                adapterType = 'wfa-cancelled';
                break;
            default:
                return null;
        }
        
        // Extract progress data (may be nested)
        const progressData = event.data as WFAProgressData | undefined;
        
        // For completed events, savedWfaId might be at the top level of data
        const savedWfaId = progressData?.savedWfaId || 
            (event.type === 'wfa-completed' ? (event.data as { savedWfaId?: string })?.savedWfaId : undefined);
        
        return {
            type: adapterType,
            jobId: event.jobId,
            data: {
                currentPeriod: progressData?.currentPeriod,
                totalPeriods: progressData?.totalPeriods,
                progress: progressData?.progress,
                currentPhase: progressData?.currentPhase,
                error: progressData?.error,
                savedWfaId,
            },
        };
    }
    
    /**
     * Create a WFA placeholder job from an event when we don't have the full job info
     */
    private createWFAPlaceholderJob(event: WFAEvent): Job {
        let status: JobStatus = 'RUNNING';
        let phase = 'Analyzing';
        
        // Handle different event types
        const eventType = event.type;
        if (eventType === 'wfa-queued') {
            status = 'QUEUED';
            phase = 'Queued';
        } else if (eventType === 'wfa-completed') {
            status = 'COMPLETED';
            phase = 'Complete';
        } else if (eventType === 'wfa-failed' || eventType === 'wfa-error') {
            status = 'FAILED';
            phase = 'Failed';
        } else if (eventType === 'wfa-cancelled') {
            status = 'CANCELLED';
            phase = 'Cancelled';
        }

        // Safely extract progress data
        const progressData = event.data as WFAProgressData | undefined;
        
        const progress: JobProgress = {
            current: progressData?.currentPeriod ?? 0,
            total: progressData?.totalPeriods ?? 0,
            percent: progressData?.progress ?? 0,
            phase: progressData?.currentPhase ?? phase,
        };

        return {
            id: event.jobId!,
            type: 'WFA_RUN',
            status,
            context: {
                symbol: 'Unknown',
                timeframe: 'Unknown',
            },
            progress,
            timing: {
                createdAt: new Date().toISOString(),
            },
        };
    }
    
    /**
     * Create a log entry from a WFA event
     */
    private createWFALogEntry(event: WFAEvent): void {
        const store = useJobsStore.getState();
        
        // Only create logs if this job is currently attached to the console
        if (store.console.jobId !== event.jobId) {
            return;
        }
        
        let message = '';
        let level: 'info' | 'warn' | 'error' | 'success' = 'info';
        
        const eventType = event.type;
        
        if (eventType === 'wfa-progress') {
            // Safely extract progress data
            const data = event.data as { currentPeriod?: number; totalPeriods?: number; currentPhase?: string } | undefined;
            const current = data?.currentPeriod ?? 0;
            const total = data?.totalPeriods ?? 0;
            const phase = data?.currentPhase ?? '';
            message = `Period ${current}/${total}: ${phase}`;
        } else if (eventType === 'wfa-completed') {
            message = 'Walk-Forward Analysis completed successfully';
            level = 'success';
        } else if (eventType === 'wfa-error') {
            const data = event.data as { error?: string } | undefined;
            message = `WFA failed: ${data?.error || 'Unknown error'}`;
            level = 'error';
        } else if (eventType === 'wfa-cancelled') {
            message = 'WFA was cancelled';
            level = 'warn';
        } else if (eventType === 'wfa-queued') {
            message = 'WFA queued';
        }
        
        if (message) {
            store.appendLogs([{
                timestamp: new Date().toISOString(),
                level,
                message,
            }]);
        }
    }
    
    /**
     * Apply a partial update to a WFA job
     */
    private applyWFAJobUpdate(
        jobId: string,
        update: ReturnType<typeof mapWFAEventToJobUpdate>
    ): void {
        const store = useJobsStore.getState();
        const currentJob = store.jobs.get(jobId);

        if (!currentJob) {
            return;
        }

        const updates: Partial<Job> = {};

        if (update.status) {
            updates.status = update.status;
        }

        if (update.progress) {
            updates.progress = {
                ...currentJob.progress,
                ...update.progress,
            };
        }

        if (update.timing) {
            updates.timing = {
                ...currentJob.timing,
                ...update.timing,
            };
        }
        
        if (update.output) {
            updates.output = update.output;
        }

        store.updateJob(jobId, updates);
    }

    /**
     * Handle an optimization event from the SSE stream
     */
    private handleOptimizationEvent(event: OptimizationEvent): void {
        // Skip non-job events (heartbeat, connected, etc.)
        if (!event.jobId || event.type === 'heartbeat' || event.type === 'connected') {
            return;
        }
        
        const store = useJobsStore.getState();
        const existingJob = store.jobs.get(event.jobId);

        if (!existingJob) {
            // Job doesn't exist - create a placeholder
            const placeholderJob = this.createPlaceholderJob(event);
            store.addJob(placeholderJob);
        }

        // Map the event to an update and apply it
        const update = mapOptimizationEventToJobUpdate(event);
        this.applyJobUpdate(event.jobId, update);
        
        // Create a log entry for the console if this job is attached
        this.createLogEntryFromEvent(event);
    }
    
    /**
     * Create a log entry from an optimization event
     */
    private createLogEntryFromEvent(event: OptimizationEvent): void {
        const store = useJobsStore.getState();
        
        // Only create logs if this job is currently attached to the console
        if (store.console.jobId !== event.jobId) {
            return;
        }
        
        let message = '';
        let level: 'info' | 'warn' | 'error' | 'success' = 'info';
        
        switch (event.type) {
            case 'progress': {
                const data = event.data as { current?: number; total?: number; progress?: number };
                if (data.current !== undefined && data.total !== undefined) {
                    message = `Progress: ${data.current}/${data.total} (${Math.round(data.progress || 0)}%)`;
                }
                break;
            }
            case 'completed':
                message = 'Optimization completed successfully';
                level = 'success';
                break;
            case 'failed':
                message = `Optimization failed: ${(event.data as { error?: string }).error || 'Unknown error'}`;
                level = 'error';
                break;
            case 'cancelled':
                message = 'Optimization was cancelled';
                level = 'warn';
                break;
            case 'queued':
                message = 'Optimization queued';
                break;
        }
        
        if (message) {
            store.appendLogs([{
                timestamp: new Date().toISOString(),
                level,
                message,
            }]);
        }
    }

    /**
     * Create a placeholder job from an event when we don't have the full job info
     */
    private createPlaceholderJob(event: OptimizationEvent): Job {
        let status: JobStatus = 'RUNNING';
        let phase = 'Optimizing';

        if (event.type === 'queued') {
            status = 'QUEUED';
            phase = 'Queued';
        } else if (event.type === 'completed') {
            status = 'COMPLETED';
            phase = 'Complete';
        } else if (event.type === 'failed') {
            status = 'FAILED';
            phase = 'Failed';
        } else if (event.type === 'cancelled') {
            status = 'CANCELLED';
            phase = 'Cancelled';
        }

        const progress: JobProgress = {
            current: 0,
            total: 0,
            percent: 0,
            phase,
        };

        // Try to extract progress from event data
        if (event.type === 'progress' && 'current' in event.data) {
            progress.current = event.data.current;
            progress.total = event.data.total;
            progress.percent = event.data.progress;
        }

        return {
            id: event.jobId,
            type: 'OPTIMIZATION_RUN',
            status,
            context: {
                symbol: 'Unknown',
                timeframe: 'Unknown',
            },
            progress,
            timing: {
                createdAt: new Date().toISOString(),
            },
        };
    }

    /**
     * Apply a partial update to a job
     */
    private applyJobUpdate(
        jobId: string,
        update: ReturnType<typeof mapOptimizationEventToJobUpdate>
    ): void {
        const store = useJobsStore.getState();
        const currentJob = store.jobs.get(jobId);

        if (!currentJob) {
            return;
        }

        const updates: Partial<Job> = {};

        if (update.status) {
            updates.status = update.status;
        }

        if (update.progress) {
            updates.progress = {
                ...currentJob.progress,
                ...update.progress,
            };
        }

        if (update.timing) {
            updates.timing = {
                ...currentJob.timing,
                ...update.timing,
            };
        }
        
        // Preserve existing output if update doesn't provide one
        // This prevents WFA artifact refs from being wiped out by
        // optimization completed events that don't include them
        if (currentJob.output && !updates.output) {
            updates.output = currentJob.output;
        }

        store.updateJob(jobId, updates);
    }
}

// Export a singleton instance for app-wide use
export const jobsIntegrationService = new JobsIntegrationService();
